import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { usePrevious } from "../../utilities/hooks";
import { useDispatch, useSelector } from "react-redux";
import { toggleParameters } from "../../actions/tools";
import {
  applyParameters,
  updateDefaultParameters,
} from "../../actions/filters";
import { track } from "../../actions/dtm";
import {
  PARAMETERS_ALL,
  PARAMETERS_NONE,
} from "../../constants/config/filters";
import * as EVENTS from "../../constants/config/dtm";
import localization from "../../data/config";
import ManageParameters from "./ManageParameters";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import Checkbox from "../CheckboxParams";
import closeIcon from "../../img/icons/dialog-close.svg";
import { extractPSTIdFromURL } from "../../utilities/filters";
import "./style.css";

const {
  manageColumnsLabel,
  setDefaultLabel,
  cancelLabel,
  updateTableLabel,
  ofLabel
} = localization;

/**
 * @name ParametersModal
 * @constant
 * @function
 * @description displays the "Choose Parameters" modal
 * @param {*} props
 */
function ManageParametersModal(props) {
  const { allParameters, classifications, show, selectedParameters } =
    useSelector((state) => ({
      allParameters: state.filters.allParameters,
      classifications: state.filters.classifications,
      show: state.tools.showParameters,
      selectedParameters: state.filters.parameters,
    }));
  const paramsLayoutRef = useRef(null);
  const dispatch = useDispatch();
  const [allParams, setAllParams] = useState(undefined),
    [showAllChecked, setShowAllChecked] = useState(allParameters.length === selectedParameters.length),
    classificationsSort = Object.keys(classifications).sort(),
    [classificationParams, setClassificationParams] = useState(undefined),
    [defaultParams, setDefaultParams] = useState(undefined),
    [disabled, setDisabled] = useState(false),
    prevShow = usePrevious(show),
    [selections, setSelections] = useState(undefined),
    [defaultStructure, setDefaultStructure] = useState(false),
    [value, setValue] = useState(""),
    pstID = extractPSTIdFromURL();
    const [isOpen, setIsOpen]=useState(false)
    let currentFocus=null;

  useEffect(() => {
    const defaultParams = JSON.parse(localStorage.getItem("defaultParameters"));
    if (!defaultParams && !classificationParams) handleInit();
    handleOpen();
    if (defaultParams) loadDefaultParameters();
  }, []);

//useEffect to hide the body scroll upon opening of overlay
  useEffect(()=>{
    if(show){
      document.body.classList.add("hide__scroll"); 
    }else
      document.body.classList.remove("hide__scroll");   
      return () => {
        document.body.classList.remove("hide__scroll");
      };
  }, [show]);
  const shiftFocusToPrimaryButton = () => {
    let primaryButton = document.querySelector(".filter-tools .primary_btn");
    if (primaryButton) {
      primaryButton.focus();
    }
  };
    const handleUserNavigation = (event) => {
      const firstElement = document.querySelector(".close__image");
      const lastElement = document.querySelector(".update");
      const manageParamsContainer = document.querySelector(".content__parameters");

      if (event.key === "Escape") {
        handleClose();
        event.preventDefault();
        // Shift focus to the primary button inside filter-tools
        shiftFocusToPrimaryButton();
      }
      if (event.key === "Enter") {
        handleClose();
        event.preventDefault();
        // Shift focus to the primary button inside filter-tools
      }
      if (event.key === "Tab") {
        // If "Tab + Shift"
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            event.preventDefault();
          } 
        } else {
          // If "Tab" without Shift or just "Tab"
          // If focus is on the last element, then return to the first
          if (document.activeElement === lastElement) {
            firstElement.focus();
            event.preventDefault();
          }
        }
        currentFocus=document.activeElement
      }
    };
  //useEffect to handle escape key and navigation by tab and shift key
   //useEffect to handle escape key and navigation by tab and shift key
  useEffect(() => {
    if(isOpen){
    // Set initial focus to the first element
    currentFocus = document.querySelector('.close__image');
    if (currentFocus) {
      currentFocus.focus();
    }

    window.addEventListener('keydown', handleUserNavigation);

    return () => {
      shiftFocusToPrimaryButton();
      window.removeEventListener('keydown', handleUserNavigation);
    };
  }
  }, [isOpen]);
  /**
   * @name handleChange
   * @function
   * @description Handles what happens when a parameter checkbox is clicked
   * @param {string} id ID of the parameter that was (de)selected
   * @param {boolean} checked Determines if the user had selected, `true` or deselected `false` the checkbox
   */
  function handleChange(id, checked) {
    let tempSelections = Array.isArray(selections) ? [...selections] : [];
    let index = tempSelections.indexOf(id);
    if (checked && index === -1) {
      tempSelections.unshift(id);
    } else if (!checked && index > -1) {
      tempSelections.splice(index, 1);
    }
    setSelections(tempSelections);
  }

  /**
   * @name handleClose
   * @function
   * @description Closes the parameter modal and resets the values to what they were when it was initially opened
   */
  function handleClose() {
    dispatch(toggleParameters());
    dispatch(track(EVENTS.CLOSE_PARAMETERS));
    setIsOpen(false)
  }

  /**
   * @name handleInit
   * @function
   * @description Sets initial values once props are retrieved
   */
  function handleInit() {
    let tempAll = [],
      tempDefault = [],
      tempParams = {};
    allParameters.forEach((parameter) => {
      tempAll.push(parameter.id);
      classificationsSort.forEach((classification) => {
        let classParams = classifications[classification];
        if (!tempParams[classification]) tempParams[classification] = [];
        if (classParams.includes(parameter.id)) {
          tempParams[classification].push(parameter);
          if (parameter.default) tempDefault.push(parameter.id);
        }
      });
    });
    classificationsSort.forEach((classification) => {
      if (tempParams[classification].length === 0)
        delete tempParams[classification];
    });
    setClassificationParams(tempParams);
    setDefaultParams(tempDefault);
    setAllParams(tempAll);
  }

  /**
   * @name loadDefaultParameters
   * @function
   * @description to load default prameters
   */
  function loadDefaultParameters() {
    const defaultParameters = JSON.parse(
      localStorage.getItem("defaultParameters")
    );
    if (defaultParameters) {
      setSelections(defaultParameters);
      dispatch(applyParameters(selections));
      dispatch(track(EVENTS.CHOOSE_PARAMETERS));
    }
  }

  /**
   * **Note:** We need to do this because the user can change the displayed parameters outside the modal
   * @name handleOpen
   * @function
   * @description Handles reset of selected params when modal is opened
   * Don't bother to check if they're different... you're going to have to loop through the array multiple times that way. this is more efficient
   */
  function handleOpen() {
    setIsOpen(true)
    setSelections(selectedParameters.map((parameter) => parameter.id));
  }

  /**
   * @name handleDefaultStructureChange
   * @function
   * @description Handles the change event of the "Set as default table structure" checkbox.
   */
  function handleDefaultStructureChange(event) {
    setDefaultStructure(event.target.checked);
  }

  /**
   * @name handleSave
   * @function
   * @description Handles the saving and application of the selected parameters
   */
  function handleSave() {
    const selectedCheckboxLabels = selections.map(selectedId => {
      const parameter = allParameters.find(param => param.id === selectedId);
      if (parameter) {
        if (parameter.delay) {
          return `${parameter.name} (${parameter.delay})`;
        } else {
          return parameter.name
        }
      }
      return '';
    });
    const concatenatedLabels = selectedCheckboxLabels.join('|');
  
      window.adobeDataLayer = window.adobeDataLayer || [];
      window.adobeDataLayer.push({
        event: "PST Table Interaction",
        pstInteractionsInfo: {
          manageparameters: concatenatedLabels
        }
      });
  
    if (defaultStructure) {
      localStorage.setItem(`defaultParams${pstID}`, selections);
    }
    const defaultParams = localStorage.getItem(`defaultParams${pstID}`);
    if (defaultParams) {
      dispatch(updateDefaultParameters(defaultParams, defaultStructure));
    }
    dispatch(toggleParameters());
    dispatch(applyParameters(selections));
    dispatch(track(EVENTS.CHOOSE_PARAMETERS));
  }

  /**
   * @name setPreSelection
   * @function
   * @description Sets the selections state to a pre-defined list of selections
   * @param {string} preset Deterimnes which preset to set the selections state to
   */
  function setPreSelection(preset) {
    switch (preset) {
      case PARAMETERS_ALL:
        setSelections(allParams);
        break;
      case PARAMETERS_NONE:
        setSelections([]);
        break;
      default:
        setSelections(defaultParams);
        break;
    }
  }

  /**
   * @name handleSelectionAll
   * @function
   * @description handles the selection of all parameters
   * @param {boolean} showAllChecked true if all parameters checkbox are clicked
   */
  const handleSelectionAll = (showAllChecked) => {
    if (!showAllChecked) {
        setShowAllChecked(true);
        setPreSelection(PARAMETERS_ALL);
      } else {
        setShowAllChecked(false);
        setPreSelection(PARAMETERS_NONE);
      }
  };

  const updateSelections = (newSelections) => {
    setSelections(newSelections);
  };

  // if there are no selections, don't attempt to render them because it will break
  if (!classifications || !classificationParams) return null;

  return (
    <div className="manage-parameters-overlay" data-show={show} aria-label={localization.manageParametersModal} >
      <div className="manage-parameters__container">
      <div className="parameters__header" >
        <h4>{manageColumnsLabel}</h4>
        <img src={closeIcon} onClick={handleClose} className="close__image" tabIndex="0" aria-label={localization.closeModal} role="button" />
      </div>
      <div className="content__parameters">
        <div className="show-all-parameters">
          <Checkbox
            title="Show all parameters"
            label="Show all parameters"
            isShowAllParamaeters="true"
            checked={showAllChecked}         
            onClick={() => handleSelectionAll(showAllChecked)}
            showAllChecked={showAllChecked}
            setShowAllChecked ={setShowAllChecked}
            selectionsLength = {selections.length}
            allParamsLength = {allParameters.length}
          />
        </div>
        <div className="parameters" ref={paramsLayoutRef}>
          <ResponsiveMasonry
            columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3 }}
          >
            <Masonry columnsCount={3}>
              {classificationsSort.map((classification, index) => (
                <ManageParameters
                  onClick={handleChange}
                  classification={classification}
                  key={"classification-" + index}
                  parameters={classificationParams[classification]}
                  selections={selections}
                  updateSelections={updateSelections}
                  showAllChecked={showAllChecked}
                  setShowAllChecked ={setShowAllChecked}
                  selectionsLength = {selections.length}
                  allParamsLength = {allParameters.length}
                />
              ))}
            </Masonry>
          </ResponsiveMasonry>
        </div>
      </div>
      <div className="parameters__footer">
        <div className="default__structure">
          <input
            className="default__structure__input checkbox-global__input"
            type="checkbox"
            checked={defaultStructure}
            onChange={handleDefaultStructureChange}
            aria-label={setDefaultLabel}
          />
          <label>{setDefaultLabel}</label>
        </div>
        <div className="footer__buttons">
          <button className="cancel" onClick={handleClose}>
            {cancelLabel}
          </button>
          <button className="update" onClick={handleSave}>
            {updateTableLabel}
            <span className="update__items__count">{`${selections.length} ${ofLabel} ${allParameters.length}`}</span>
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

ManageParametersModal.propTypes = {
  allParameters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  applyParameters: PropTypes.func,
  classificationsSort: PropTypes.object,
  labelChoose: PropTypes.string,
  labelDefault: PropTypes.string,
  labelDeselectAll: PropTypes.string,
  labelSelectAll: PropTypes.string,
  show: PropTypes.bool,
  toggleParameters: PropTypes.func,
  track: PropTypes.func,
  setParametersAll: PropTypes.string,
  selectedParameters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
};

export default ManageParametersModal;
